'use client';

import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const EFFECTIVE_DATE = 'May 7, 2026';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <Navigation />

      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Terms of{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Service
            </span>
          </h1>
          <p className="text-gray-400">Effective {EFFECTIVE_DATE}</p>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-gray-800/40 border border-gray-700 rounded-xl p-8 md:p-12 space-y-10 text-gray-300 leading-relaxed">

          <div>
            <p>
              These Terms of Service ("Terms") govern your access to and use
              of Reclipt (the "Service"), provided by Reclipt ("Reclipt",
              "we", "us"). By creating an account or using the Service you
              agree to these Terms. If you are using the Service on behalf
              of an organization, you represent that you have authority to
              bind that organization to these Terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">1. The Service</h2>
            <p>
              Reclipt provides AI-powered tools to upload, transcribe,
              search, clip, and export spoken-word video content. Features
              and limits available to your account depend on your
              subscription plan.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">2. Eligibility and accounts</h2>
            <p>
              You must be at least 13 years old (16 in the European Economic
              Area) to use the Service. You are responsible for keeping
              your login credentials secure and for all activity under your
              account. Notify us promptly at{' '}
              <a href="mailto:support@reclipt.io" className="text-blue-400 hover:text-blue-300 underline">
                support@reclipt.io
              </a>{' '}
              if you suspect unauthorized access.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">3. Your content</h2>
            <p>
              You retain ownership of the videos, audio, transcripts, clips,
              and other content you upload or generate using the Service
              ("Your Content"). You grant Reclipt a worldwide, non-exclusive
              license to host, store, transmit, transcribe, index, process,
              and display Your Content solely as needed to operate and
              improve the Service for you. This license ends when you
              delete the content or your account, except for backups
              retained for a reasonable period.
            </p>
            <p className="mt-4">
              You represent that you have all necessary rights and
              permissions to upload Your Content and that it does not
              violate any law or third-party right.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">4. Acceptable use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Upload content you do not have the rights to use, including infringing or illegal content.</li>
              <li>Upload content that is defamatory, harassing, hateful, sexually exploitative, or otherwise harmful.</li>
              <li>Attempt to reverse-engineer, scrape, or interfere with the operation of the Service.</li>
              <li>Use the Service to build a competing product, or to create or train AI/ML models from data you do not own.</li>
              <li>Bypass usage limits, rate limits, or access controls.</li>
              <li>Send spam, malware, or otherwise abuse the Service or its users.</li>
            </ul>
            <p className="mt-4">
              We may suspend or terminate accounts that violate these rules.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">5. Subscriptions and payments</h2>
            <p>
              Paid plans are billed in advance on a recurring basis. You
              authorize us to charge your payment method for the fees
              associated with your selected plan, including applicable
              taxes. You can cancel at any time from your account settings;
              cancellation takes effect at the end of the current billing
              period. Fees are non-refundable except where required by law.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">6. Third-party services</h2>
            <p>
              Reclipt integrates with third-party services such as Google
              (sign-in and Drive import), Clerk, Supabase, Wasabi, Coconut,
              and Railway. Your use of these integrations is also subject to
              the third party's own terms and privacy policies, which we do
              not control.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">7. Intellectual property</h2>
            <p>
              The Service, including its software, design, branding, and
              documentation, is owned by Reclipt and protected by
              intellectual-property laws. We grant you a limited,
              non-exclusive, non-transferable license to use the Service in
              accordance with these Terms. All rights not expressly granted
              are reserved.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">8. Termination</h2>
            <p>
              You may stop using the Service at any time by deleting your
              account. We may suspend or terminate your access if you breach
              these Terms, if required by law, or if continued provision of
              the Service to you is no longer commercially viable. On
              termination, the rights granted to you end and we may delete
              Your Content after a reasonable period.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">9. Disclaimers</h2>
            <p>
              The Service is provided "as is" and "as available" without
              warranties of any kind, whether express or implied, including
              warranties of merchantability, fitness for a particular
              purpose, non-infringement, or accuracy of AI-generated
              outputs. Transcripts, clips, and other automated outputs may
              contain errors; you are responsible for reviewing them before
              relying on or publishing them.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">10. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, Reclipt and its
              suppliers will not be liable for any indirect, incidental,
              consequential, special, exemplary, or punitive damages, or
              any loss of profits, revenue, data, or goodwill arising from
              or related to your use of the Service. Reclipt's total
              aggregate liability for any claim relating to the Service
              will not exceed the amount you paid Reclipt in the twelve
              months preceding the event giving rise to the claim, or one
              hundred US dollars (US$100), whichever is greater.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">11. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Reclipt and
              its officers, employees, and agents from any claim, loss, or
              demand (including reasonable legal fees) arising from Your
              Content or your violation of these Terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">12. Changes to the Service or Terms</h2>
            <p>
              We may update or modify the Service and these Terms from time
              to time. Material changes to these Terms will be announced
              through the Service or by email. Continued use of the Service
              after a change becomes effective constitutes acceptance of the
              updated Terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">13. Governing law</h2>
            <p>
              These Terms are governed by the laws of England and Wales,
              without regard to its conflict-of-laws principles. The courts
              of England and Wales will have exclusive jurisdiction over any
              dispute arising out of or relating to these Terms or the
              Service, except that either party may seek injunctive relief
              in any court of competent jurisdiction.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-4">14. Contact</h2>
            <p>
              Questions about these Terms can be sent to{' '}
              <a href="mailto:support@reclipt.io" className="text-blue-400 hover:text-blue-300 underline">
                support@reclipt.io
              </a>
              .
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
